import Select from '@/components/Shared/Select.vue';
import Tooltip from '@/components/Shared/Tooltip.vue';
import {
  can,
  cleanName,
  CustimooOrderFlowStatuses,
  formatDate,
  formatShortDate,
  logError,
  setPageTitle,
  timeAgo,
} from '@/composables';
import { useVueQuery } from '@/composables/useVueQuery';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  DashboardItem,
  DashboardRequest,
  FactoryProduct,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
  Urgency,
} from '@/models';
import { useMainStore } from '@/store';
import { useDashboardStore } from '@/store/dashboard';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon, KeyIcon } from '@heroicons/vue/24/solid';
import { createPopper } from '@popperjs/core';
import { useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, h, nextTick, ref, VNode, watch } from 'vue';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import orderHoldIcon from '@/assets/img/images/stop.svg';

declare global {
  interface Window {
    virtualReference?: {
      getBoundingClientRect: () => DOMRect;
    };
  }
}
interface OrderPage {
  items: DashboardItem[];
  total: number;
  nextPage?: number;
}

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  renderer?: (value: any, row: any) => string | VNode | number;
}

export const useDashboard = () => {
  const store = useMainStore();
  const dashboardStore = useDashboardStore();
  const appURL = import.meta.env.VITE_VUE2_APP_URL;

  const {
    dashboardData,
    dashboardTabs,
    permissionLoaded,
    orderAdmins,
    dashboardQuickFilters,
    loading,
    sidebarOpen,
  } = storeToRefs(store);

  const { modeTabs, currentMode, orderList } = storeToRefs(dashboardStore);

  const datatable = ref(null);

  const queryClient = useQueryClient();

  const currentTab = ref('all_items');
  const paneDefaultTab = ref<string>(localStorage.getItem('activeDetailsTab') ?? 'timeline');
  const selectedFilter = ref<string | null>(null);
  const createManualOrder = ref<boolean>(false);
  const assignFactoryModal = ref<boolean>(false);
  const selectedOrderDetails = ref<OrderDetail>(null);
  const isSidepaneLoading = ref<boolean>(false);
  const priceBreakdownModal = ref<boolean>(false);
  const selectedOrderItem = ref<DashboardItem>(null);
  const selectedUrgentOrderFilter = ref<string | null>(null);
  const internalNoteModal = ref(false);
  const internalNoteRow = ref(null);
  const taskSearchQuery = ref(null);
  let defParams: DashboardRequest = {
    items_for: currentTab.value ?? 'all_items',
    sort_by: 'order_no',
    sort_order: 'desc',
    page: 1,
    search: '',
    per_page: 10,
    new_dashboard: true
  };
  const showDashboardActions = ref<boolean>(false);
  const dashboardActionsRef = ref<any>(null);

  const defParamsString = JSON.stringify({ dashboardRequest: defParams });
  const currentDashboardModel: Record<string, DashboardRequest> = JSON.parse(
    localStorage.getItem('dashboardRequest') ?? defParamsString
  );
  const currentActiveRow = ref<DashboardItem | undefined | null>(JSON.parse(localStorage.getItem('currentRow')));
  const openDetails = ref<boolean>(JSON.parse(localStorage.getItem('openDetails')) ?? false)
  const fetchOrder = ref<boolean>(true);
  const current_filters = ref<DashboardRequest>(currentDashboardModel.dashboardRequest);

  let popperInstance: any = null;

  const { data, error, isFetching, isPending } = useVueQuery<OrderPage, Error>(
    'orders',
    async () => {
      const params: DashboardRequest = {
        ...defParams,
        ...current_filters.value,
        page: current_filters.value.page,
      };
      await store.getDashboardData(params, dashboardQuickFilters.value);
      if (currentActiveRow.value && fetchOrder.value && openDetails.value) {
        await handleCellClick({
          row: currentActiveRow.value,
          column: {
            key: 'id',
            label: 'id',
          },
        });
      }
      const currentData = dashboardData.value?.items;
      return {
        items: currentData?.data ?? [],
        total: currentData?.total ?? 0,
      };
    },
    {
      queryKey: [
        'orders',
        currentTab,
        selectedFilter,
        selectedUrgentOrderFilter,
        dashboardQuickFilters,
        defParams.sort_by,
        defParams.sort_order,
        defParams.search,
        current_filters.value.page,
      ],
      refetchOnWindowFocus: false,
    }
  );

  const tabs = [
    { id: 'timeline', label: 'Timeline' },
    { id: 'detail', label: 'Detail' },
    { id: 'files', label: 'Files' },
  ];
  const filteredOrderDetailTabs = computed(() => {
    return can('sharepoint-files') ? tabs : tabs.filter((tab) => tab.id !== 'files');
  });
  const canAssignOrderAdmin = computed(() => can('assign-order-administrator'));
  const currentRow = ref<DashboardItem | null>(null);
  const previousSelectedRow = ref<DashboardItem | null>(null);

  const selectedRow: ComputedRef<DashboardItem | null> = computed(() => {
    if (currentActiveRow.value && fetchOrder.value && datatable.value && !previousSelectedRow.value && openDetails.value) {
      fetchOrder.value = false;
      scrollToCurrentRow();
      return currentActiveRow.value
    }
    else if (openDetails.value === false && datatable.value && !currentRow.value && currentActiveRow.value) {
      previousSelectedRow.value = JSON.parse(JSON.stringify(currentActiveRow.value));
      currentActiveRow.value = null;
      scrollToCurrentRow();
      return previousSelectedRow.value;
    }
    else if (currentRow.value) {
      return currentRow.value;
    } else if (previousSelectedRow.value) {
      return previousSelectedRow.value;
    } else {
      return null;
    }
  });

  const canViewShippingAddress = computed(() => can('order-listing-shipping-address-column'));
  const canViewMerchant = computed(() => can('order-listing-merchant-column'));
  const canViewPaymentStatus = computed(() => can('view-payment-status'));
  const canViewFactory = computed(() => can('order-listing-factory-column'));

  const filteredModeTabs = computed(() => {
    return modeTabs.value.filter((tab) => {
      if (tab.id === 'calendar') {
        return can('can-see-calendar');
      }
      if (tab.id === 'my-tasks') {
        return can('view-my-tasks');
      }
      return true;
    });
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      case 'delayed':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };
  const orderOptions = computed(() =>
    orderList.value.map((order) => ({
      ...order,
      label: order.customer_reference_no
        ? `${order.order_no} (${order.customer_reference_no})`
        : order.order_no,
    }))
  );
  const handlePriceBreakDownModalCloseEvent = async (fetch_order_items = false) => {
    priceBreakdownModal.value = false;
    if (fetch_order_items) {
      fetchOnSave(true);
      await handleOrderUpdated()
    }
  };
  const onOrderSearch = async (query: string, loading: (b: boolean) => void) => {
    loading(true);
    await dashboardStore.searchOrders(query);
    loading(false);
  };

  const onModeTabChange = (tabId: string) => {
    dashboardStore.setCurrentMode(tabId);

    if (tabId === 'calendar' && !dashboardStore.calendarInitialized) {
      dashboardStore.initializeCalendar();
    }
  };

  const handleViewChanged = (payload: {
    view: string;
    selected_date: string;
    selected_holiday_country_code: string;
  }) => {
    dashboardStore.setCurrentView(payload.view);
    dashboardStore.setSelectedDate(new Date(payload.selected_date));
    dashboardStore.setSelectedHolidayCountryCode(payload.selected_holiday_country_code);
  };

  const onCalendarFilterChange = (filters: any) => {
    if (currentMode.value === 'calendar') {
      if (
        filters &&
        typeof filters === 'object' &&
        ('items_for' in filters || 'quick_filters' in filters)
      ) {
        if (!filters.quick_filters || Object.keys(filters.quick_filters).length === 0) {
          dashboardStore.setCalendarFilters(null);
        } else {
          dashboardStore.setCalendarFilters(filters);
        }
        if (filters.items_for === 'my_assigned_orders') {
          dashboardStore.setCalendarFilters({
            ...filters,
            my_assigned_orders: true,
          });
        }
      } else {
        dashboardStore.setCalendarFilters(null);
      }
    } else {
      selectedFilter.value = typeof filters === 'string' ? filters : null;
    }
  };

  const factoryModalClose = () => {
    assignFactoryModal.value = false;
    selectedOrderItem.value = null;
  };

  const tableData = computed(() => {
    return data.value?.items ?? [];
  });

  const totalItems = computed(() => {
    return data.value?.total ?? 0;
  });


  const fetchRecords = async (data: any): Promise<void> => {
    defParams = {
      items_for: currentTab.value ?? 'all_items',
      sort_by: data?.sortColumn ?? 'order_no',
      sort_order: data?.sortOrder ?? data?.sort_order,
      page: data?.page ?? 1,
      per_page: data?.pageSize ?? data?.per_page,
      search: data?.searchQuery ?? '',
      new_dashboard: true
    };

    current_filters.value = {
      ...current_filters.value,
      ...defParams,
    };

    localStorage.setItem(
      'dashboardRequest',
      JSON.stringify({ dashboardRequest: current_filters.value })
    );

    await queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const onTabChange = async (tab: string): Promise<void> => {
    if (tab === 'calendar') {
      currentTab.value = tab;
    } else {
      currentTab.value = tab;
      defParams = {
        ...defParams,
        items_for: currentTab.value,
        sort_by: 'order_no',
        sort_order: 'desc',
        page: 1,
      };
      let localFilterInfo = localStorage.getItem('dashboardRequest');
      current_filters.value = {
        ...JSON.parse(localFilterInfo ?? defParamsString).dashboardRequest,
        ...defParams,
      };
    }
  };

  const fetchOnSave = async (fetch: boolean) => {
    if (fetch) {
      let localFilterInfo: Record<string, DashboardRequest> = JSON.parse(
        localStorage.getItem('dashboardRequest') ?? defParamsString
      );
      current_filters.value = localFilterInfo.dashboardRequest;
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (currentRow.value && tableData.value && Array.isArray(tableData.value)) {
        const updated = tableData.value.find(row => row.id === currentRow.value.id);
        if (updated) {
          currentRow.value = updated;
          localStorage.setItem('currentRow', JSON.stringify(updated));
          const orderData = await store.getOrderDetail(currentRow.value.order.id);
          selectedOrderDetails.value = orderData;
        }
      }
      scrollToCurrentRow();
    }
  };

  const loginCustomer = async (customer_id: number): Promise<void> => {
    loading.value = true;
    await http
      .get(`/customer/information/${customer_id}`)
      .then((res) => {
        let token = res.data.access_token;
        if (res.data.user.company_url) {
          const url = res.data.user.company_url + '/#/';
          window.open(`${url}admin/login?token=${token}`);
        }
      })
      .catch((err) => {
        logError(err);
      });
    loading.value = false;
  };

  const getUrgencyClass = (urgency: Urgency, id: number): string => {
    switch (urgency) {
      case 'VIP':
        return 'bg-green-300 !text-[#686666] !p-0 ';
      case 'Urgent':
        return 'order_cancel !text-[#686666] !p-0';
      case 'Extra Attention':
        return 'bg-blue-300 !text-[#686666] !p-0';
      default:
        return `!p-0 ${id && selectedRow.value && selectedRow.value.id === id ? 'bg-gray-300' : ''}`;
    }
  };

  const visibleColumns = computed(() => {
    const baseColumns = [
      {
        key: currentTab.value === 'quote_requested_items' ? 'order_no' : 'quote_no',
        label: currentTab.value === 'quote_requested_items' ? `Quote No` : `Order No`,
        sortable: true,
        sortKey: 'order_no',
        cellClass: (row: DashboardItem) => {
          return currentTab.value !== 'quote_requested_items' && can('show-urgency-filters')
            ? getUrgencyClass((row as DashboardItem).order?.urgency, row?.id)
            : '!p-0 ' + (selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '');
        },
        renderer: (val: any, row: DashboardItem) => {
          const rawSymbol = row.order?.order_type_symbol;
          const urgency = row.order?.urgency;
          const isManualOrder = row.order?.additional_fields?.is_manual_order;
          const orderNumber = row.order
            ? `${isManualOrder ? 'M_#' : '#'}${row.order.order_no}`
            : '';
          const cleanedSymbol = rawSymbol?.replace(/i/g, '');
          const allowedSymbols = ['S', 'R'];
          const showSymbol = rawSymbol && allowedSymbols.includes(cleanedSymbol);
          const padding = '0.75rem';




          const children = [
            orderNumber,
            showSymbol &&
            h(
              'span',
              {
                style: {
                  color: 'red',
                  fontWeight: 'bold',
                  marginLeft: '4px',
                  fontSize: '1rem',
                },
              },
              cleanedSymbol
            ),
          ];
          if (row.internal_comment) {
            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  content: JSON.parse(row.internal_comment).comment,
                },
                {
                  trigger: () =>
                    h(
                      'button',
                      {
                        class: 'ml-4 cursor-pointer align-middle',
                        style:
                          'display:inline-flex;vertical-align:middle;background:none;border:none;padding:0;',
                        onClick: (e) => {
                          e.stopPropagation();
                          openInternalNoteModal(row);
                        },
                        title: 'View/Add Internal Note',
                      },
                      [
                        h(ChatBubbleBottomCenterTextIcon, {
                          class: 'size-5 text-[#2600D8]',
                        }),
                      ]
                    ),
                }
              )
            );
          }

          // Show on-hold icon with tooltip containing latest reason (if order is on_hold)
          if (row.status === 'on_hold') {
            let holdReasonText = 'N/A';

            try {
              const raw = row.on_hold_reason;
              const parsed = typeof raw === 'string' && raw.trim()
                ? JSON.parse(raw)
                : raw;

              if (Array.isArray(parsed) && parsed.length > 0) {
                const last = parsed[parsed.length - 1];

                // New format: { status, reason, timestamp, user_id }
                if (last && typeof last === 'object') {
                  holdReasonText = last.reason || 'N/A';
                }
              }
            } catch (err) {
              holdReasonText = row?.on_hold_reason || 'N/A';
            }

            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  contentClass: 'whitespace-normal max-w-sm !p-4',
                },
                {
                  trigger: () =>
                    h('img', {
                      src: orderHoldIcon,
                      class: 'ml-2 flex-shrink-0', // Added flex-shrink-0
                      style: {
                        width: '20px',      // Explicit width
                        height: '20px',     // Explicit height
                        minWidth: '20px',   // Prevent shrinking
                        minHeight: '20px',  // Prevent shrinking
                        objectFit: 'contain' // Maintain aspect ratio
                      }
                    }),
                  default: () =>
                    h('div', {  class: 'flex flex-col gap-2 max-w-sm w-full z-[10000]' }, [
                      h('div', {
                        class: 'font-bold text-[18px] text-gray-800 pb-2 z-[10000] border-b border-gray-200 mb-2 mt-2 break-words',
                      }, 'Order on hold'),
                      h('div', { class: 'text-[14px] leading-relaxed max-w-sm z-[10000]' }, [
                        h('div', { class: 'font-semibold text-gray-700 mb-2 z-[10000]' }, 'Reasoning:'),
                        h('div', { class: 'text-gray-600 pl-2 whitespace-normal break-words max-w-full z-[10000]' }, holdReasonText),
                      ]),
                    ])
                }
              )
            );
          }

          const cellChilds = [

            h(
              'span',
              {
                class: 'text-sm',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              },
              children
            ),
            row.reorder_order_ids && h(
              'span',
              {
                class: 'text-xs bg-primary p-1 rounded-[20px] text-center text-white',
              },
              'Reorder'
            ),
          ].filter(Boolean)
          const cellContent = h(
            'div',
            {
              class: 'w-full h-full flex flex-col gap-2',
              style: {
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                padding: padding,
              },
            },
            cellChilds
          );

          if (urgency && can('show-urgency-filters')) {
            return h(
              Tooltip,
              {
                placement: 'right',
                content: `${urgency ? urgency.replace(/_/g, ' ') : 'normal'} Order`,
                widthClass: 'w-full',
              },
              {
                trigger: () => cellContent,
              }
            );
          } else {
            return cellContent;
          }
        },
      },
      {
        key: '',
        label: openDetails.value ? 'By' : 'Customer',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const { customer_name, customer_email } = row.order;

          let mainContent;
          const customerName = cleanName(customer_name);
          if (openDetails.value || customerName.length > 30) {
            // Show initials with tooltip
            let displayVal = '';
            let tooltipVal = '';

            if (customerName?.trim()?.length) {
              tooltipVal = customerName;
              // Get initials (first letter of each word, max 2)
              const initials = customerName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              displayVal = `${initials}..`;
            } else if (customer_email?.trim()?.length) {
              tooltipVal = customer_email;
              displayVal = `${customer_email.slice(0, 2).toUpperCase()}...`;
            }

            mainContent = h(
              Tooltip,
              {
                placement: 'top',
                content: tooltipVal,
              },
              {
                trigger: () => h('span', { class: 'whitespace-nowrap font-semibold' }, displayVal),
              }
            );
          } else {
            // Show full name or email
            mainContent = h(
              'span',
              { class: 'whitespace-nowrap font-semibold' },
              customerName?.trim()?.length ? customerName : customer_email
            );
          }

          // "Login as Customer" button with icon and tooltip
          const loginButton = h(
            Tooltip,
            {
              placement: 'right',
              content: 'Login as Customer',
            },
            {
              trigger: () =>
                h(
                  'button',
                  {
                    class:
                      'bg-[#00B087] text-white text-sm font-medium px-2 p-1 rounded-[4px] cursor-pointer flex gap-1 items-center',
                    onClick: () => {
                      loginCustomer(row.order.customer_id);
                    },
                  },
                  [
                    h(KeyIcon, {
                      class: 'w-4 h-4 text-white',
                    }),
                  ]
                ),
            }
          );

          return h('span', { class: 'flex gap-1 items-center justify-between' }, [
            mainContent,
            loginButton,
          ]);
        },
      },
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        sortKey: 'status_updated_at',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const dateValue = row.order.created_at ?? Date.now().toString();
          if (openDetails.value) {
            return formatShortDate(dateValue);
          }
          return formatDate(dateValue);
        },
      },
      {
        key: '',
        label: 'Est. Shipping Date',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          if (!row.est_shipping_date) return 'N/A';
          const dateVal = Array.isArray(row.est_shipping_date)
            ? row.est_shipping_date[row.est_shipping_date.length - 1]
            : row.est_shipping_date;
          return selectedRow.value ? formatShortDate(dateVal) : formatDate(dateVal);
        },
      },
      canViewMerchant.value && {
        key: '',
        label: 'Merchant',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) =>
            row.order.billing_company_name
          ? `${row.order.company_name} / ${row.order.billing_company_name}`
          : row.order.company_name
      },
      {
        key: '',
        label: 'Customer Reference',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) => row.order.customer_reference_no ?? 'N/A',
      },
      canViewFactory.value && {
        key: 'factory_name',
        label: 'Factory',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const factoryName = row?.factory_name ?? 'N/A';
          if (openDetails.value || factoryName.length > 20) {
            // Show first three letters with tooltip
            return h(
              Tooltip,
              {
                placement: 'top',
                content: factoryName,
              },
              {
                trigger: () =>
                  h(
                    'span',
                    { class: 'whitespace-nowrap font-semibold uppercase' },
                    factoryName.slice(0, 3)
                  ),
              }
            );
          }
          // Show full name if details are closed
          return h('span', { class: 'whitespace-nowrap font-semibold' }, factoryName);
        },
      },
      canViewPaymentStatus.value && {
        key: '',
        label: 'Invoice Status',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300 capitalize' : 'capitalize';
        },
        renderer: (val: any, row: DashboardItem) => {
          const status = row.order.stripe_invoice_status;
          return (status && status.trim() !== '') ? status : 'N/A';
        },
      },
      {
        key: 'items_total_quantity',
        label: 'Qty',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => row.items_total_quantity ?? 'N/A',
      },
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const mark_dqc=row.order?.mark_dqc;
          const orderId = row.order?.id;
          const rowId = row?.id;


          const hasReadPermission = can('read-dqc-status');
          const hasWritePermission = can('write-dqc-status');

         const dqcCheckbox = h(
            Tooltip,
            {
              placement: 'right',
              content: mark_dqc
                ? 'DQC marked on ' +
                  formatTimestampWithBrowserTimezone(row?.order?.dqc_marked_at)
                : hasWritePermission
                  ? 'Click to mark DQC approved'
                  : 'View only (no permission to modify)',
            },
            {
              trigger: () =>
                h(
                  'label',
                  {
                    class: [
                      'flex items-center justify-center text-xs font-medium rounded-xl select-none transition-all duration-150',
                      mark_dqc
                        ? 'border-green-500 text-green-700'
                        : 'border-black-700 text-black-700',
                      hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                    ],
                    onClick: (e: Event) => e.stopPropagation(), // prevent row click
                  },
                  [
                    h('input', {
                      type: 'checkbox',
                      checked: mark_dqc,
                      disabled: !hasWritePermission || isSidepaneLoading.value,

                    class: [
                      'custom-dqc-checkbox w-4 h-4 rounded-md  border-2 border-black-700 transition-all duration-150 relative',
                      mark_dqc
                        ? 'border-green-500 text-green-700'
                        : 'border-black-700 text-black-700',
                      hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                    ],
                      onChange: (event: Event) =>
                        hasWritePermission ? toggleDQC(event, orderId,rowId) : null,
                    }),
                  ]
                ),
            }
          );


          const showDropdown =
            row.status === 'order_approve' &&
            !row.factory_id &&
            can('assign-factory-to-shareable-orders');

          const handleClick = async (e) => {
            e.stopPropagation();
            if (!showDropdown) return;

            try {
              const orderData = await store.getOrderDetail(row.order_id, row.id);
              const factory_products = orderData.items?.[0]?.factory_products ?? [];
              const { factory_products: _, ...rest } = row;
              assignFactoryModal.value = true;
              selectedOrderItem.value = {
                ...rest,
                factory_products: factory_products.map((prod) => ({
                  ...prod,
                  selected_factory: prod.selected_factory ?? null,
                })),
              };
            } catch (error) {
              logError('Failed to load order details', error);
            }
          };
        return h(
          'div',
          {
            class: 'flex items-center justify-center gap-2',
          },
          [
            h(
              'button',
              {
                tabindex: '-1',
                onClick: handleClick,
                class: `${row.status} w-max ${
                  openDetails.value ? 'max-w-[8rem] p-1' : 'p-2'
                } mx-auto rounded-[4px] flex justify-between items-center`,
              },
              [
              CustimooOrderFlowStatuses[row.status] ?? (row.status === 'on_hold' ? 'On Hold' : row.status),
                showDropdown &&
                  h(
                    'button',
                    {
                      class: 'px-1',
                      onClick: async () => {
                        try {
                          const orderData = await store.getOrderDetail(
                            row.order_id,
                            row.id
                          );
                          const factory_products =
                            orderData.items?.[0]?.factory_products ?? [];
                          const { factory_products: _, ...rest } = row;
                          assignFactoryModal.value = true;
                          selectedOrderItem.value = {
                            ...rest,
                            factory_products: factory_products.map((prod) => ({
                              ...prod,
                              selected_factory: prod.selected_factory ?? null,
                            })),
                          };
                        } catch (error) {
                          logError('Failed to load order details', error);
                        }
                      },
                    },
                    [h(ChevronDownIcon, { class: 'text-[#9197B3] size-4 ml-1' })]
                  ),
              ].filter(Boolean)
            ),
            (hasReadPermission || hasWritePermission) ? h('div', { class: 'ml-2' }, [dqcCheckbox]) : null,
          ].filter(Boolean)
        );
        },
      },
      {
        key: 'days_since_action',
        label: openDetails.value ? 'Since Action' : 'Days Since Action',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
        },
        sortable: true,
        sortKey: 'status_updated_at',
        renderer: (val: any, row: DashboardItem) =>
          timeAgo(row.order.updated_at ?? Date.now().toString()),
      },
    ];

     // Conditionally add Amount column if permission is granted
    if (can('view-amount-column')) {
      baseColumns.splice(
        baseColumns.findIndex(col => col.key === 'status'), // Insert before Status column
        0,
        {
          key: 'order_total_price',
          label: 'Amount',
          sortable: false,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            const price = row.order_total_price;
            return (price !== undefined && price !== null)
              ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)
              : 'N/A';
          }
        }
      );
    }

    if (!openDetails.value) {
      return [
        ...baseColumns,
        canViewShippingAddress.value && {
          key: '',
          label: 'Shipping Address',
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          sortable: false,
          renderer: (val: any, row: DashboardItem) => {
            if (!row.order.shipping_address) return '';
            return `${row.order.shipping_address.address1 || ''} ${row.order.shipping_address.address2 || ''} ${row.order.shipping_address.city || ''}
                  ${row.order.shipping_address.zip_code || ''} ${row.order.shipping_address.country || ''}`;
          },
        },
        canAssignOrderAdmin.value && {
          key: 'admin',
          label: 'Administrator',
          sortable: false,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-gray-300' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            if (!row.factory_id && row.order.order_administrator?.name)
              return row.order.order_administrator.name;
            if (!row.factory_id) return 'N/A';
            const filteredOrderAdmins = orderAdmins.value.filter((admin: OrderAdminUser) =>
              admin.factory_ids?.includes(row.factory_id)
            );
            return h(
              'div',
              {
                onClick: (e: Event) => e.stopPropagation(),
                class: 'w-full h-full',
              },
              [
                h(Select, {
                  options: filteredOrderAdmins,
                  modelValue: row.order.order_administrator,
                  'track-by': 'id',
                  label: 'name',
                  'onUpdate:modelValue': async (newValue: OrderAdminUser) => {
                    row.order.order_administrator = newValue;
                    const payload = {
                      user_id: newValue ? newValue.id : null,
                      order_ids: row.order.id,
                    };
                    await store.updateOrderAdmin(payload);

                    await fetchRecords({
                      ...defParams,
                      ...current_filters.value,
                    });
                  },
                  class: 'white-space-nowrap min-w-[200px] max-w-max',
                  placeholder: 'Assign Admin',
                  'append-to-body': true,
                }),
              ]
            );
          },
        },
      ].filter(Boolean);
    }

    return baseColumns;
  });

  const columns = computed(() => visibleColumns.value);

  function hasAllValues(obj: Record<string, any>): boolean {
    return Object.values(obj).every(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }
  const getQuickFilterData = async (filter: any) => {
    if (filter && typeof filter === 'object' && hasAllValues(filter)) {
      const { quick_filters } = filter;
      defParams = {
        ...defParams,
        page: 1,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = quick_filters ? { ...quick_filters } : {};
    } else {
      defParams = {
        ...defParams,
        page: 1,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = {};
      selectedUrgentOrderFilter.value =
        filter?.urgent_order !== undefined
          ? filter?.urgent_order
          : selectedUrgentOrderFilter.value;
    }
  };

  watch(
    [() => dashboardStore.queryParams, () => dashboardStore.calendarFilterPayload],
    async ([newParams, newPayload], [oldParams, oldPayload]) => {
      if (currentMode.value === 'calendar') {
        if (newParams !== oldParams || newPayload !== oldPayload) {
          await dashboardStore.fetchCalendarData();
        }
      }
    },
    { immediate: true, deep: true }
  );
  // watch(() => [
  //   currentTab,
  //   selectedFilter,
  //   selectedUrgentOrderFilter,
  //   dashboardQuickFilters,
  //   defParams.sort_by,
  //   defParams.sort_order,
  //   defParams.search,
  //   current_filters.value.page], ([newVal]) => { console.log(newVal) })

  const currentOrderItem: ComputedRef<OrderDetailItem | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.find(
        (item) => item.factory_id === currentRow.value.factory_id
      )
      : null;
  });

  const currentOrderItemIndex: ComputedRef<number | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.findIndex(
        (item) => item.factory_id === currentRow.value.factory_id
      )
      : null;
  });

  const handleCellClick = async ({ row }: { row: DashboardItem; column: Column }) => {
    setPageTitle(`#${row.order.order_no}`);
    localStorage.setItem('currentRow', JSON.stringify(row));
    localStorage.setItem('openDetails', 'true');
    openDetails.value = true;
    currentRow.value = row;
    isSidepaneLoading.value = true;
    sidebarOpen.value = false;
    try {
      const orderData = await store.getOrderDetail(row.order.id, row.id);
      selectedOrderDetails.value = orderData;
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const handleOrderMoved = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(currentRow.value.order.id);
        selectedOrderDetails.value = orderData;
      }

      await fetchOnSave(true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const handleOrderUpdated = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(currentRow.value.order.id);
        selectedOrderDetails.value = orderData;
      }

      await fetchOnSave(true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const openInternalNoteModal = (row) => {
    internalNoteRow.value = row;
    internalNoteModal.value = true;
  };

  const handleInternalNoteClose = async () => {
    internalNoteModal.value = false;
    internalNoteRow.value = null;
    await fetchOnSave(true);
  };

  const scrollToCurrentRow = () => {
    const localRow = currentRow.value ?? previousSelectedRow.value;
    if (!localRow || !datatable.value) return;

    nextTick(() => {
      try {
        const tableElement = datatable.value.$el;
        const tableBody = tableElement?.querySelector('tbody');

        if (!tableBody) return;

        const rows = tableBody.querySelectorAll('tr');
        let targetRow: HTMLElement | null = null;

        rows.forEach((row) => {
          const rowData = row.getAttribute('data-row-id') || row.dataset.rowId;
          if (rowData === localRow.id.toString()) {
            targetRow = row;
          }
        });
        if (targetRow) {
          targetRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          targetRow.style.transition = 'background-color 0.3s ease';
          targetRow.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            targetRow.style.backgroundColor = '';
          }, 1000);
        }
      } catch (error) {
        console.warn('Error scrolling to selected row:', error);
      }
    });
  };

  const openOrderPriceModal = async () => {
    const orderData = await store.getOrderDetail(currentRow.value.order.id);
    selectedOrderDetails.value = orderData;
    priceBreakdownModal.value = true;
  };

  const onDetailsClose = () => {
    selectedOrderDetails.value = null;
    previousSelectedRow.value = { ...currentRow.value };
    localStorage.removeItem('currentRow')
    currentRow.value = null;
    sidebarOpen.value = true;
    openDetails.value = false;
    localStorage.setItem('openDetails', 'false');
  };
  const detailsTabChange = (tabId: string) => {
    paneDefaultTab.value = tabId;
    localStorage.setItem('activeDetailsTab', tabId ?? 'timeline')
  }
  const handleRowSelected = (row: DashboardItem) => {
    localStorage.setItem('currentRow', JSON.stringify(row));
    if (!openDetails.value) {
      localStorage.setItem('openDetails', 'false');
      openDetails.value = false;
    }

    currentActiveRow.value = row;
  }
  const openDashboardActions = async ({ event, row }: { event: MouseEvent; row: any }) => {
    event.preventDefault();

    const { clientX, clientY } = event;

    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }

    window.virtualReference = {
      getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0),
    };
    showDashboardActions.value = true;

    localStorage.setItem("currentRow", JSON.stringify(row));
    currentRow.value = row;

    if (currentRow.value) {
      localStorage.setItem("openDetails", "true");

      if (openDetails.value) {
        sidebarOpen.value = false;
      }

      await nextTick();

      if (dashboardActionsRef.value && window.virtualReference) {
        popperInstance = createPopper(window.virtualReference, dashboardActionsRef.value, {
          placement: "bottom-start",
          strategy: "fixed",
          modifiers: [
            { name: "offset", options: { offset: [0, 6] } },
            { name: "preventOverflow", options: { boundary: "viewport" } },
            { name: "flip", options: { fallbackPlacements: ["top-start", "right-start", "left-start"] } },
          ],
        });
      }

      try {
        const orderData = await store.getOrderDetail(row.order.id, row.id);
        selectedOrderDetails.value = orderData;
      } finally {
        isSidepaneLoading.value = false;
      }
    } else {
      localStorage.setItem("openDetails", "false");
      openDetails.value = false;
    }
  };


  const handleCloseAction = () => {
    showDashboardActions.value = false;
    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }
  };


  const makeReorderMessage = (factory_product: FactoryProduct) => {
    let message = ''
    if (factory_product && factory_product.reorder_data) {
      let reorder_data = factory_product.reorder_data;
      message = `Reorder of ${reorder_data.order_number ? 'order' : 'order_id'} #${reorder_data.order_number ? reorder_data.order_number : reorder_data.order_id} `;
      if (reorder_data.roster_change === true && reorder_data.design_change === true) {
        message += 'with design and roster changes.';
      } else if (reorder_data.roster_change === true && reorder_data.design_change === false) {
        message += 'with roster changes.';
      } else if (reorder_data.roster_change === false && reorder_data.design_change === true) {
        message += 'with design changes.';
      } else {
        message += 'with no modifications';
      }

      if (!reorder_data.order_number) {
        message += ' (order not confirmed)'
      }
    }
    return message;
  }

  const toggleDQC = async (event: Event,orderId,rowId) => {

    const target = event.target as HTMLInputElement;
    const intendedStatus = target.checked;
      try {
        // Proceed only if confirmed
        target.checked = intendedStatus;
        const payload = {
          dqc_approved: intendedStatus,
        };
        await store.updateOrderDQCStatus(orderId, payload);

        if (
            dashboardQuickFilters.value?.filter_by == 'marked_dqc' ||
            dashboardQuickFilters.value?.filter_by == 'not_marked_dqc'
          ) {
            await fetchOnSave(true);

        }else{

            const orderData = await store.getOrderDetail(orderId);
            const updated = data.value.items.find(
              row => row.id === rowId
            );
            if (updated && updated.order) {
              updated.order.mark_dqc = orderData.mark_dqc;
              updated.order.dqc_marked_at = orderData.dqc_marked_at;
            }
        }

      } catch (error) {
        console.error('Error updating DQC status:', error);
        target.checked = !intendedStatus;
      } finally {
        // No additional action needed here since we handle state in try/catch
      }
  };

  const exportOrderPrices = async (fromDate: string, endDate: string): Promise<void> => {

    let defParams: DashboardRequest = {
      items_for: currentTab.value ?? 'all_items',
      sort_by: 'order_no',
      sort_order: 'desc',
      page: 1,
      search: '',
      per_page: 10,
      new_dashboard: true
    };

    const currentDashboardModel: Record<string, DashboardRequest> = JSON.parse(
      localStorage.getItem('dashboardRequest') ?? defParamsString
    );
    const current_filters = ref<DashboardRequest>(currentDashboardModel.dashboardRequest);
    

    const params: DashboardRequest = {
      ...defParams,
      ...current_filters.value,
      page: null,
      per_page: null    
    }

    await store.exportOrderPrices(params, dashboardQuickFilters.value, {
      fromDate: fromDate,
      endDate: endDate,
    });
  };

  // watch([selectedOrderDetails], () => {
  //   if (currentRow.value) {
  //     scrollToCurrentRow();
  //   }
  // });
  return {
    appURL,
    dashboardTabs,
    permissionLoaded,
    datatable,
    currentTab,
    createManualOrder,
    assignFactoryModal,
    selectedOrderDetails,
    isSidepaneLoading,
    priceBreakdownModal,
    selectedOrderItem,
    internalNoteModal,
    internalNoteRow,
    currentRow,
    current_filters,
    filteredOrderDetailTabs,
    filteredModeTabs,
    orderOptions,
    tableData,
    totalItems,
    columns,
    currentOrderItem,
    currentOrderItemIndex,
    error,
    isFetching,
    isPending,
    currentMode,
    dashboardStore,
    sidebarOpen,
    dashboardData,
    paneDefaultTab,
    taskSearchQuery,
    getStatusColor,
    handlePriceBreakDownModalCloseEvent,
    onOrderSearch,
    onModeTabChange,
    handleViewChanged,
    onCalendarFilterChange,
    factoryModalClose,
    fetchRecords,
    onTabChange,
    fetchOnSave,
    getQuickFilterData,
    handleCellClick,
    handleOrderMoved,
    handleOrderUpdated,
    handleInternalNoteClose,
    openOrderPriceModal,
    onDetailsClose,
    detailsTabChange,
    handleRowSelected,
    currentActiveRow,
    openDashboardActions,
    showDashboardActions,
    dashboardActionsRef,
    openDetails,
    handleCloseAction,
    selectedRow,
    makeReorderMessage,
    exportOrderPrices
  };
};
